import {
  payment3_paymentmethodpb,
  payment3_paymentmethod_coordinatorpb,
  payment3_commonpb,
} from "@ftdr/payment3_paymentmethod_coordinator-js-client";
import { payment3_paymentus_gatewaypb } from "@ftdr/payment3_paymentus_secure-js-client";
import { Mode } from "react-hook-form";
import { PaymentusFormStyles, PaymentusSetupType } from "./components";
import { BraintreeSetup } from "./components/payment-processors/paypal-processor/const";
import { StripeSetupType } from "./components/payment-processors/stripe-card-processor/components/stripe-elements/consts";
import { WalletJSClient } from "@ftdr/wallet-js-client";

export type RequireAtLeastOne<T, Keys extends keyof T = keyof T> = Pick<
  T,
  Exclude<keyof T, Keys>
> &
  {
    [K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>;
  }[Keys];

export interface IIdentity {
  contractIdentity?: RequireAtLeastOne<
    payment3_paymentmethodpb.IContractIdentity,
    "contractID" | "contractPaymentID"
  >;
  customerIdentity?: payment3_paymentmethodpb.ICustomerIdentity;
  replacementIdentity?: payment3_paymentmethodpb.IApplianceReplacementIdentity;
  dispatchIdentity?: payment3_paymentmethodpb.IDispatchIdentity;
  orderIdentity?: payment3_paymentmethodpb.IOrderIdentity;
  quoteIdentity?: payment3_paymentmethodpb.IQuoteIdentity;
  externalContractIdentity?: payment3_paymentmethodpb.IExternalContractIdentity;
  externalCustomerIdentity?: payment3_paymentmethodpb.IExternalCustomerIdentity;
}

export type RegistrationRequest = payment3_paymentmethod_coordinatorpb.ICreatePaymentMethodRegistrationRequest;
export type MethodSetupRequest = payment3_paymentmethod_coordinatorpb.ICreatePaymentMethodRequest;
export type RegistrationResponse = payment3_paymentmethod_coordinatorpb.ICreatePaymentMethodRegistrationResponse;
export type MethodSetupResponse = payment3_paymentmethod_coordinatorpb.ICreatePaymentMethodResponse & {
  paymentMethod?: {
    details?: {
      cc?: {
        brandName?: string;
      } | null;
    } | null;
  } | null;
};
export type SecureResponse = payment3_paymentus_gatewaypb.IPaymentusRegistration;
export * from "./components/payment-processors/types";

export type UuidType = {
  traceId: string;
  spanId: string;
};

export type RequiredOwnerType = {
  ownerIdentity: {
    type: payment3_paymentmethodpb.OwnerIdentityType | null;
  } & RequireAtLeastOne<
    IIdentity,
    | "contractIdentity"
    | "customerIdentity"
    | "replacementIdentity"
    | "dispatchIdentity"
    | "orderIdentity"
    | "quoteIdentity"
    | "externalContractIdentity"
    | "externalCustomerIdentity"
  >;
};

export type FormsSetupType = {
  stripeForm?: {
    mode?: Mode;
    reValidateMode?: Exclude<Mode, "onTouched" | "all">;
    fieldsSetup: StripeSetupType;
  };
  paymentusForm?: {
    mode?: Mode;
    reValidateMode?: Exclude<Mode, "onTouched" | "all">;
    fieldsSetup: PaymentusSetupType;
    formStyles?: PaymentusFormStyles;
  };
  braintreeForm?: {
    fieldsSetup?: BraintreeSetup;
    /**
     * The brand prop is used in the registration process and is used to set the company brand name in the Paypal screen.
     */
    brand?: payment3_commonpb.Brand;
  };
};

export type StyleType = "AHSRebrand" | "default" | "HBW210";

export enum WalletCheckboxValue {
  SAVE_IN_WALLET = "save_in_wallet",
  SET_AS_DEFAULT = "set_as_default",
}

export type SetWalletCheckboxProps = {
  /**
   * This prop should be set to either "save_in_wallet" or "set_as_default", depending on which checkbox you want to display
   */
  showWalletCheckbox?: WalletCheckboxValue;
  /**
   * ContractID passed to MFE via props, required to use the Set as default payment method checkbox.
   */
  contractID?: string | null;
  /**
   * CustomerID passed to MFE via props, required to use the Set as default payment method checkbox.
   */
  customerID?: string | null;
  /**
   * PropertyID is an optional prop passed to MFE. It can be provided to call 'SetAsDefault', but it is not required.
   */
  propertyID?: string | null;
  /**
   * Determines whether the checkbox should be checked by default on the initial render
   */
  defaultChecked?: boolean;
  /**
   * Label for the "Set as default" checkbox
   */
  label?: string;
  /**
   * The instance of initialized wallet js client with token refresher and token to be used for the WalletService.
   */
  walletJsClientPassThrough?: WalletJSClient;
  /**
   * The Source Type Code is used for wallet backend integration
   * default: CSC
   */
  sourceTypeCode?: undefined;
};
